import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound, Eye, EyeOff, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Brand } from '@/components/ui/Brand';

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [checking, setChecking] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = useCallback(async () => {
    if (!token) { setTokenInfo({ valid: false, reason: 'missing' }); setChecking(false); return; }
    try {
      const res = await axios.get(`${API}/auth/validate-reset-token/${encodeURIComponent(token)}`);
      setTokenInfo(res.data);
    } catch {
      setTokenInfo({ valid: false, reason: 'network_error' });
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => { validate(); }, [validate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match.'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Password reset successfully — please log in.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderContent = () => {
    if (checking) {
      return (
        <div className="flex items-center gap-3 text-slate-600" data-testid="reset-checking">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Validating reset link...</span>
        </div>
      );
    }

    if (!tokenInfo?.valid) {
      const reasonText = {
        expired: 'This reset link has expired. Please request a new one.',
        invalid_or_used: 'This reset link is invalid or has already been used.',
        user_not_found: 'The associated account no longer exists.',
        missing: 'No reset token was provided in the link.',
        network_error: 'We couldn\'t verify the link right now. Please try again.',
      }[tokenInfo?.reason] || 'This reset link cannot be used.';
      return (
        <div className="space-y-4 text-center" data-testid="reset-invalid">
          <div className="p-3 inline-flex rounded-full bg-amber-100">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
          </div>
          <p className="text-sm text-slate-700">{reasonText}</p>
          <Button onClick={() => navigate('/login')} className="bg-blue-900 hover:bg-blue-800" data-testid="reset-back-to-login">
            Back to login
          </Button>
        </div>
      );
    }

    if (success) {
      return (
        <div className="space-y-4 text-center" data-testid="reset-success">
          <div className="p-3 inline-flex rounded-full bg-emerald-100">
            <CheckCircle className="h-6 w-6 text-emerald-600" />
          </div>
          <p className="text-sm text-slate-700">Your password has been reset. Redirecting to login...</p>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
        <p className="text-sm text-slate-600">
          Resetting the password for <span className="font-medium">{tokenInfo.email}</span>.
        </p>
        <div>
          <Label htmlFor="new-password" className="text-slate-700 font-medium">New Password</Label>
          <div className="relative mt-1.5">
            <Input
              id="new-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 8 chars, 1 upper, 1 lower, 1 number, 1 special"
              required
              autoFocus
              className="pr-10"
              data-testid="reset-new-password-input"
            />
            <button type="button" onClick={() => setShowPassword(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
              aria-label={showPassword ? 'Hide password' : 'Show password'}>
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label htmlFor="confirm-password" className="text-slate-700 font-medium">Confirm New Password</Label>
          <Input
            id="confirm-password"
            type={showPassword ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Re-enter password"
            required
            className="mt-1.5"
            data-testid="reset-confirm-password-input"
          />
        </div>
        <Button type="submit" disabled={submitting} className="w-full bg-blue-900 hover:bg-blue-800" data-testid="reset-submit-button">
          {submitting ? 'Resetting...' : 'Reset password'}
        </Button>
      </form>
    );
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage:
          'url(https://images.unsplash.com/photo-1654783865377-f059d186c8e4?auto=format&fit=crop&w=2400&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="reset-password-page"
    >
      <div className="absolute inset-0 bg-black/40"></div>
      <div className="relative z-10 w-full max-w-md p-8">
        <div className="bg-white rounded-md shadow-lg p-8 border border-slate-200">
          <div className="mb-6 text-center">
            <h1 className="text-3xl font-bold tracking-tight mb-1"><Brand /></h1>
            <p className="text-sm text-slate-500 inline-flex items-center gap-2 justify-center mt-2">
              <KeyRound className="h-4 w-4" /> Reset Password
            </p>
          </div>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
